import type { IFilesystem } from '@livx.cc/wcli/core';
import type { AgentTool, ToolContext } from './tools';
import { formatCatalog } from './catalog';
import { splitFrontmatter, grabField } from './frontmatter';

export interface CommandInfo {
  name: string;
  description: string;
  path: string;
}

/**
 * Resolves a name in the *other* half of the merged slash namespace (CC parity: `/x` is one
 * namespace whether `x` is a `commands/x.md` or a `skills/x/SKILL.md`). On a miss in its own
 * registry, a tool calls this to find the sibling and learn how to expand it. Injected by the
 * caller (Agent.prepare) so commands.ts and skills.ts stay mutually decoupled (no circular import).
 */
export type SiblingResolver = (name: string) => Promise<{ path: string; stripFrontmatter: boolean } | undefined>;

/** Parse a command template's `description:` frontmatter + return the body that follows. */
function parseFrontmatter(md: string): { description?: string; body: string } {
  const { block, body } = splitFrontmatter(md);
  return { description: grabField(block, 'description'), body };
}

/**
 * Expand a command template's argument placeholders:
 *  - `$ARGUMENTS` → all args (every occurrence)
 *  - `$1`…`$9`    → positional args (whitespace-split; missing → empty)
 * If the body has neither placeholder, `args` is appended as a trailing block (back-compat).
 */
export function expandTemplate(body: string, args: string): string {
  if (!/\$ARGUMENTS|\$[1-9]/.test(body)) return args ? `${body}\n\n${args}` : body;
  const toks = args.trim() ? args.trim().split(/\s+/) : [];
  return body.split('$ARGUMENTS').join(args).replace(/\$([1-9])/g, (_m, n) => toks[Number(n) - 1] ?? '');
}

/** Inline `@path` references (CC-style) with the file's contents, fenced. Non-existent paths stay literal. */
async function embedFiles(text: string, fs: IFilesystem): Promise<string> {
  const refs = [...new Set([...text.matchAll(/(?:^|\s)@(\S+)/g)].map((m) => m[1]))];
  let out = text;
  for (const ref of refs) {
    try {
      const content = await fs.readFile(ref);
      out = out.split('@' + ref).join(`\n\n--- @${ref} ---\n${content}\n--- end @${ref} ---\n`);
    } catch { /* not a readable file — leave the literal @ref (could be an email, decorator, etc.) */ }
  }
  return out;
}

/**
 * Read a slash entry's file and return its expanded text: arg placeholders filled, then `@file`
 * refs embedded. `stripFrontmatter` drops the YAML head (commands feed only their body); skills
 * feed the whole SKILL.md (frontmatter is part of the instructions). Shared by commands + skills
 * so both halves of the merged namespace expand identically (CC parity).
 */
export async function expandEntry(
  fs: IFilesystem,
  path: string,
  args: string,
  opts: { stripFrontmatter?: boolean } = {},
): Promise<string> {
  const raw = await fs.readFile(path);
  const text = opts.stripFrontmatter ? parseFrontmatter(raw).body : raw;
  return embedFiles(expandTemplate(text, args), fs);
}

/** Read a command file and return its expanded body: arg placeholders filled, then `@file` refs embedded. */
export async function expandCommand(fs: IFilesystem, cmd: CommandInfo, args: string): Promise<string> {
  return expandEntry(fs, cmd.path, args, { stripFrontmatter: true });
}

/**
 * Discover slash commands under `dir` (each `<dir>/<name>.md`) and produce:
 *  - `catalog`: a names+descriptions block for the system prompt,
 *  - `tool`: a `SlashCommand` tool that returns a command's expanded template body.
 *
 * Commands are reusable prompt templates (like Claude Code slash commands) — just
 * VFS files. The template body is returned (user args appended, and `$ARGUMENTS`
 * substituted if present) so the model can act on it. Mirrors loadSkills.
 */
/** Scan `dir`(s) for `<dir>/<name>.md` command templates (first dir wins on duplicate names). */
export async function scanCommands(fs: IFilesystem, dir: string | string[]): Promise<CommandInfo[]> {
  const commands: CommandInfo[] = [];
  const seen = new Set<string>();
  for (const d of Array.isArray(dir) ? dir : [dir]) {
    if (!await fs.exists(d)) continue;
    for (const entry of await fs.readDir(d)) {
      if (!entry.endsWith('.md')) continue;
      const name = entry.replace(/\.md$/, '');
      if (seen.has(name)) continue; // first dir wins
      seen.add(name);
      const path = `${d}/${entry}`;
      const fm = parseFrontmatter(await fs.readFile(path));
      commands.push({ name, description: fm.description || '', path });
    }
  }
  return commands;
}

export async function loadCommands(
  fs: IFilesystem,
  dir: string | string[],
  opts: {
    /** @deprecated no-op — the catalog is full + task-independent (stable prompt prefix). */
    relevanceHint?: string;
    /** @deprecated no-op — use `budgetBytes`. */
    max?: number;
    /** Catalog byte budget (default 6000); over it descriptions trim, names never drop. */
    budgetBytes?: number;
    sibling?: SiblingResolver;
  } = {},
): Promise<{ commands: CommandInfo[]; catalog: string; tool?: AgentTool }> {
  const commands = await scanCommands(fs, dir);
  if (commands.length === 0) return { commands, catalog: '' };

  const catalog = formatCatalog(commands, {
    title: '## Slash commands (reusable prompt templates)',
    footer: 'To run one, call the `SlashCommand` tool with its name (and optional `args`); it returns the expanded template to act on.',
    namePrefix: '/',
    budgetBytes: opts.budgetBytes,
  });

  const tool: AgentTool = {
    name: 'SlashCommand',
    description: 'Run a slash command (or skill) by name — returns its expanded prompt template/instructions (with your `args` substituted/appended). Resolves both the command and skill namespaces. Use when a task matches a listed command or skill.',
    parameters: {
      type: 'object',
      required: ['name'],
      properties: { name: { type: 'string' }, args: { type: 'string', description: 'arguments to fill the template' } },
    },
    async run({ name, args }, ctx: ToolContext) {
      const slug = String(name ?? '').replace(/^\//, '');
      let c = commands.find((x) => x.name === slug);
      let fresh: CommandInfo[] | undefined;
      if (!c) { // miss → rescan: commands created mid-conversation are runnable without a reprepare
        fresh = await scanCommands(ctx.fs, dir);
        c = fresh.find((x) => x.name === slug);
        if (c) commands.push(c);
      }
      if (c) return expandCommand(ctx.fs, c, String(args ?? ''));
      // still a miss → merged namespace: try the sibling (skills) registry before failing.
      const sib = await opts.sibling?.(slug);
      if (sib) return expandEntry(ctx.fs, sib.path, String(args ?? ''), { stripFrontmatter: sib.stripFrontmatter });
      return `Error: no command or skill named '${slug}'. Available commands: ${(fresh ?? []).map((x) => x.name).join(', ') || '(none)'}`;
    },
  };
  return { commands, catalog, tool };
}
